import { get } from "lodash"
import { isLocal, parseMatrixDate } from "@/utils/helpers/formatters"
import { MemberOnlineConfig, SubPage } from "@/types/General"

// These are injected by webpack
declare const __ENV__: string
declare const __NOAUTH__: string
declare const __MIDDLEWARE_PROFILE__: {
  localBaseUrl: string
  apiRoute: string
  key: string
  msg: string
}

function getConfig(path: string | string[], def?: any) {
  return get(window.GESB_MemberOnlineConfig, path, def)
}

function getScopedConfig(scope: string, path: string | null = null) {
  if ("string" === typeof path) {
    return getConfig(`${scope}.${path}`)
  }

  return getConfig(scope)
}

export function getRoutingMethod() {
  return getConfig("routingMethod", "hash")
}

export function getBasename() {
  return getConfig("basename")
}

export function getMatrixApiConfig(path: string | null = null) {
  const config = getScopedConfig("matrixApi", path)

  if (__ENV__ === "development" && isLocal()) {
    const { localMiddleware } = getMiddlewareConfig()
    if (typeof config === "string") {
      // Strip the origin, keep just the path, prepend proxy key
      const url = new URL(config)
      return `${localMiddleware}${url.pathname}`
    }
  }

  return config
}

export function getUserDataConfig(path: string | null = null) {
  return getScopedConfig("userData", path)
}

export function getBaseUrl() {
  if (isLocal()) {
    return __MIDDLEWARE_PROFILE__.localBaseUrl
  }

  const host = getConfig("host")
  if (host) {
    validateHost(host)
    return "https://" + host
  }

  const fallbackHost = window.location.host
  return `${window.location.protocol}//${fallbackHost}`
}

// Allowlist of trusted hosts for SSRF protection
const ALLOWED_HOSTS = [
  "mol-uat.gesb.wa.gov.au",
  "mol.gesb.wa.gov.au",
  "mol-uat.gesb-web.matrix.squiz.cloud",
  "mol.gesb-web.matrix.squiz.cloud",
  // Add other trusted hosts as needed
] as const

/**
 * Validates that a host is in the allowlist to prevent SSRF attacks
 * @throws Error if host is not in allowlist
 */
function validateHost(host: string): void {
  if (!ALLOWED_HOSTS.includes(host as (typeof ALLOWED_HOSTS)[number])) {
    throw new Error(
      `Invalid host configuration: "${host}" is not in the allowed hosts list`
    )
  }
}

function isAllowedErrorReportingHost(hostname: string): boolean {
  if ((ALLOWED_HOSTS as readonly string[]).includes(hostname)) {
    return true
  }
  // CMS may target the same host as the member app (including local dev hostnames).
  if (typeof window !== "undefined" && hostname === window.location.hostname) {
    return true
  }
  return false
}

function normalizeErrorReportingUrl(urlString: string): string | undefined {
  try {
    const url = new URL(urlString.trim())
    if (url.protocol !== "https:") {
      return undefined
    }
    if (!isAllowedErrorReportingHost(url.hostname)) {
      return undefined
    }
    const path = url.pathname.replace(/\/$/, "")
    return `${url.origin}${path}`
  } catch {
    return undefined
  }
}

const ERROR_REPORTING_INGEST_TOKEN = "t0ganhrOiLTIiHfJu8RiDLpX4g2TMbIZ"

function getMiddlewareConfig() {
  let apiRoute = null,
    localMiddleware = ""

  if (__ENV__ === "development" && isLocal()) {
    const profile = __MIDDLEWARE_PROFILE__
    apiRoute = profile.apiRoute
    localMiddleware = profile.key
  }

  // If no middleware is set, use the default linkApi (PROD)
  if (!apiRoute) {
    apiRoute = getConfig("linkApi")
  }

  return { apiRoute, localMiddleware }
}

export function getMiddlewareRoot() {
  const { apiRoute, localMiddleware } = getMiddlewareConfig()
  return getBaseUrl() + localMiddleware + apiRoute
}

export function getSessionLoggedOut() {
  return getConfig("loggedOut")
}

export function getLogoutUrl() {
  return getConfig("logoutUrl")
}

export function getErrorMessage() {
  return getConfig("errorMsg")
}

export function getLinkUrl(name: string) {
  return getConfig(`links.${name}`, "")
}

export function getPage() {
  return getConfig("page")
}

export function getOktaAdminUrl(id: string) {
  return `https://${getConfig("oktaDomain")}/admin/user/profile/view/${id}`
}

export function getOktaIssuerMfa() {
  return `https://${getConfig("oktaIssuerMfa")}`
}

// NOTE: this is the client ID for the Okta Transaction MFA Application
export function getOktaClientIdMfa() {
  return getConfig("oktaClientIdMfa")
}

export function getOktaScope() {
  return getConfig("oktaScope")
}

export function getGlobalAlert() {
  return getConfig("globalAlert")
}

export function getBsbLookupUrl(): string | undefined {
  // Matrix injects the directory URL under matrixApi (also proxies locally via getMatrixApiConfig)
  return getMatrixApiConfig("bsbLookupUrl")
}

export function getRegisterAlert() {
  return getConfig("registerAlert")
}

export function getGenericAlert() {
  return getConfig("genericAlert")
}

export function getSubPages(): SubPage[] {
  return getConfig("subpages") ?? []
}

export function getErrorReportingUrl(): string | undefined {
  const configured = getConfig("errorReportingUrl")
  if (typeof configured !== "string" || !configured.trim()) {
    return undefined
  }
  return normalizeErrorReportingUrl(configured)
}

/** Posts client error payloads only to the CMS-configured, host-allowlisted ingest URL. */
export function postErrorReport(payload: Record<string, unknown>): void {
  const baseUrl = getErrorReportingUrl()
  if (!baseUrl) {
    return
  }

  const requestUrl = `${baseUrl}?token=${ERROR_REPORTING_INGEST_TOKEN}`
  const allowedRequestUrls = new Set([requestUrl])
  if (!allowedRequestUrls.has(requestUrl)) {
    return
  }

  fetch(requestUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {})
}

export function getSeminarDetailsUrl() {
  return getConfig("seminarDetailsUrl")
}

export function getWebinarDetailsUrl() {
  return getConfig("webinarDetailsUrl")
}

export function getInvestStartEnd() {
  // array used here to allow keys with "." in it
  const mdStart = getConfig(["siteMetadata", "site.noInvestChoiceStart"], {
    isDefault: true,
  })
  const mdEnd = getConfig(["siteMetadata", "site.noInvestChoiceEnd"], {
    isDefault: true,
  })

  const startDate = !mdStart.isDefault && parseMatrixDate(mdStart.value)
  const endDate = !mdEnd.isDefault && parseMatrixDate(mdEnd.value)

  return { startDate, endDate }
}

export function getRemoveSustainablePlan() {
  return getConfig("siteMetadata.removeSustainablePlan")?.value === "true"
}

export function isFortnightlyPensionPaymentEnabled() {
  // Dotted Matrix key; use array notation like getInvestStartEnd().
  return (
    getConfig(["siteMetadata", "site.fortnightlyPensionPayments"])?.value ===
    "true"
  )
}

export function getSnippet(str: string) {
  return getConfig(`snippets.${str}`, "")
}

export function getLastLogin() {
  const lastLogin = getConfig("userData.metadata.previousLogin")
  if (lastLogin) {
    return lastLogin.isDefault ? false : lastLogin.value
  }
  return false
}

export function getGitBridges() {
  return getConfig("gitBridges", null)
}

export function getTransferBalanceCap() {
  return getConfig("data.transferBalanceCap")
}

export function getAiaUrl(): string {
  return getConfig("aiaUrl")
}

export function getAllowFormSubmissions(): boolean {
  if (!getConfig("userData.isAdmin")) {
    return true
  }

  return getConfig("webmasterFormSubmission")
}

export function getFastLinkConfig(): MemberOnlineConfig["fastlink"] {
  return getConfig("fastlink")
}

export function getDevBypassHeader() {
  return isLocal() && __NOAUTH__ === "true" ? true : null
}
